import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { useReportQuery } from "../shared/useReportQuery";
import { useSearchGate } from "../shared/useSearchGate";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { formatMetric, METRIC_LABELS } from "../shared/format";
import {
  hasTenderMetrics,
  hasUntenderedMetric,
  orderMetricsForFinancialGrid,
} from "../shared/tenderBreakdown";
import { TOKEN_TENDER_CAVEAT, UNTENDERED_LEGEND } from "../shared/metricDefinitions";
import { MetricKey, ReportDimension } from "../api/types";

// The 9 fixed metrics the engine returns for FinancialByDimension. The grid renders whatever is
// in metrics[]; this list only drives the summary-card order (NetSales headline first).
const SUMMARY_METRICS: MetricKey[] = [
  "NetSales",
  "Gross",
  "Tax",
  "Discount",
  "Refunds",
  "Tip",
  "Surcharge",
  "OrderCount",
  "TransactionCount",
];

// The supported group-by dimensions. Clerk is enabled once the in-flight engine deploy lands.
// Sub-Event ("Show") is still coming (engine throws NotSupported until then), so it is NOT listed.
const DIMENSIONS: { value: ReportDimension; label: string; columnHeader: string }[] = [
  { value: "Event", label: "Event", columnHeader: "Event" },
  { value: "Vendor", label: "Vendor", columnHeader: "Vendor" },
  { value: "Location", label: "Location", columnHeader: "Location" },
  { value: "VendorType", label: "Vendor Type", columnHeader: "Vendor Type" },
  { value: "Day", label: "Day", columnHeader: "Date" },
  { value: "Clerk", label: "Clerk", columnHeader: "Clerk" },
];

const TOOLTIP =
  "Consolidated Financial report from the NEW unified reporting engine. Pick a Group by dimension " +
  "(Event, Vendor, Location, Vendor Type, Day, or Clerk) — the first column and the rollup swap automatically. " +
  "Net Sales is the canonical real-money net that matches the Financial reports; Gross = order subtotal + tax " +
  "(tips and the service fee are separate). Money in USD.";

// Resolve a ?dimension= value against the supported list; anything unknown falls back to Event.
const initialDimension = (raw: string | null): ReportDimension =>
  DIMENSIONS.find((d) => d.value === raw)?.value ?? "Event";

// Consolidated Financial report. One page, one metric set, dimension chosen at runtime — the engine
// returns the standard metadata-driven envelope so MetricGrid renders it with no column logic.
export default function FinancialReport() {
  const [filters, setFilters] = useState(defaultFilters);
  // ?dimension=Vendor|VendorType deep-links this page as the replacement for the OLD Vendor Sales /
  // Vendor Type Sales reports (RP-2155/2159) — /reports-new/sales-by-vendor redirects here. Read
  // once as the initial value; the select owns it from there.
  const [searchParams] = useSearchParams();
  const [dimension, setDimension] = useState<ReportDimension>(
    () => initialDimension(searchParams.get("dimension"))
  );

  // Nothing fetches until the user clicks Search; the snapshot carries the chosen dimension too.
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters, dimension });
  const state = useReportQuery(
    submitted
      ? toQueryParams("FinancialByDimension", submitted.filters, { dimension: submitted.dimension })
      : null
  );

  // Column header/rollup reflect the SUBMITTED dimension (what the data is), not the pending draft.
  const activeDimension = submitted?.dimension ?? dimension;
  const cfg = DIMENSIONS.find((d) => d.value === activeDimension) ?? DIMENSIONS[0];

  // Per-tender $ + txn-count columns (RP-2153…59): the engine advertises them as ordinary metrics;
  // reorder into the OLD reports' layout — the gross-$ tender block, then the "# of Transactions"
  // block (see shared/tenderBreakdown.ts). Memoized so the grid's columnDefs identity is stable.
  const gridData = useMemo(
    () =>
      state.data ? { ...state.data, metrics: orderMetricsForFinancialGrid(state.data) } : null,
    [state.data]
  );

  return (
    <ReportPageShell
      title="Financial"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      actions={
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          Group by
          <select
            value={dimension}
            onChange={(e) => setDimension(e.target.value as ReportDimension)}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {DIMENSIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {(data) => (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {SUMMARY_METRICS.filter((m) => data.metrics.includes(m)).map((m) => (
              <StatCard
                key={m}
                label={METRIC_LABELS[m]}
                value={formatMetric(m, data.totals[m])}
                emphasis={m === "NetSales"}
              />
            ))}
          </div>
          <MetricGrid
            data={gridData ?? data}
            keyColumnHeader={cfg.columnHeader}
            exportName={`Financial_${activeDimension}`}
            // The caveats also ride into the CSV so an exported file explains its own numbers.
            csvExtra={[
              ...(hasTenderMetrics(data) ? [`NOTE: ${TOKEN_TENDER_CAVEAT}`] : []),
              ...(hasUntenderedMetric(data) ? [`NOTE: ${UNTENDERED_LEGEND}`] : []),
            ]}
          />
          {hasTenderMetrics(data) && (
            <p className="mt-2 text-xs text-gray-500">
              <span className="font-semibold">Note:</span> {TOKEN_TENDER_CAVEAT}
            </p>
          )}
          {/* Why the new Gross runs ABOVE the old report's "Total Gross Sales" (RP-2155/2159).
              Gated on the engine actually advertising the column, so it can't describe a column
              that isn't on screen. */}
          {hasUntenderedMetric(data) && (
            <p className="mt-2 text-xs text-gray-500">
              <span className="font-semibold">Untendered:</span> {UNTENDERED_LEGEND}
            </p>
          )}
        </>
      )}
    </ReportPageShell>
  );
}
